/**
 * @file PickupLocationCard.tsx
 * @description Пункт самовывоза в форме способа получения (phase19.24
 * шаг 5.1, §2 «Состояния»).
 *
 * Только чтение: точка берётся из настроек склада магазина — тот же
 * источник, что у витрины. Менять адрес прямо в заказе нельзя, иначе письмо
 * клиенту и страница магазина разойдутся; ссылка ведёт в настройки складов.
 *
 * Три состояния приходят из одного места, поэтому живут здесь, а не в
 * вызывающей форме: грузим, справочник упал (`error` + «Повторить»), адрес не
 * заполнен (`warning` + ссылка). Пустой график — не ошибка: в письме напишем
 * «время выдачи уточнит менеджер».
 */

import { Link } from "react-router";

import { Button } from "../ui/button";
import { DocumentMetaGrid } from "../ui/DocumentMetaGrid";
import { NoticeStrip } from "../ui/NoticeStrip";
import { SectionLabel } from "../ui/SectionLabel";
import { Skeleton } from "../ui/skeleton";
import {
  isPickupLocationReady,
  type ShopPickupLocationDto,
} from "../../lib/api-shop-pickup";

interface Props {
  location: ShopPickupLocationDto | null;
  loading: boolean;
  /** Текст ошибки справочника; `null` — запрос прошёл. */
  error: string | null;
  onRetry: () => void;
  /** Slug магазина для ссылки в настройки складов; `undefined` — без ссылки. */
  shopSlug?: string;
  /**
   * Проблема формы, относящаяся к пункту («письмо отправить нельзя»):
   * показываем прямо здесь — менеджер ищет причину там, где принимал решение.
   */
  issue?: string | null;
}

export function PickupLocationCard({
  location,
  loading,
  error,
  onRetry,
  shopSlug,
  issue,
}: Props) {
  const settingsHref = shopSlug ? `/shop/${shopSlug}/wms/warehouses` : null;

  if (loading) {
    return <Skeleton className="h-24 w-full rounded-xl" />;
  }

  if (error) {
    return (
      <NoticeStrip
        tone="error"
        action={
          <Button variant="outline" className="min-h-12" onClick={onRetry}>
            Повторить
          </Button>
        }
      >
        Не удалось получить пункт самовывоза: {error}. Заказ создать можно —
        адрес подставится, когда справочник ответит.
      </NoticeStrip>
    );
  }

  if (!isPickupLocationReady(location)) {
    return (
      <NoticeStrip
        tone={issue ? "error" : "warning"}
        action={
          settingsHref ? (
            <Button asChild variant="outline" className="min-h-12">
              <Link to={settingsHref}>Заполнить в настройках складов</Link>
            </Button>
          ) : undefined
        }
      >
        {issue ??
          "Пункт самовывоза не заполнен: у склада магазина нет адреса. Клиент не узнает, куда ехать."}
      </NoticeStrip>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel
        trailing={
          settingsHref ? (
            <Link
              to={settingsHref}
              className="text-sm text-primary hover:underline"
            >
              Изменить в настройках
            </Link>
          ) : undefined
        }
      >
        Пункт самовывоза · из настроек склада
      </SectionLabel>
      <DocumentMetaGrid
        items={[
          { label: "Точка", value: location.name || "Склад магазина" },
          { label: "Адрес", value: location.address },
          {
            label: "Время работы",
            value: location.workingHours || "не указано",
            tone: location.workingHours ? "default" : "warning",
          },
          { label: "Телефон", value: location.phone || "не указан", mono: !!location.phone },
        ]}
      />
      {location.workingHours ? null : (
        <p className="text-xs text-on-surface-variant">
          График не заполнен — в письме напишем «время выдачи уточнит менеджер».
        </p>
      )}
      {/* Пункт заполнен, но сервер всё равно отказал (адрес стёрли между
          загрузкой формы и отправкой) — причина стоит здесь же. */}
      {issue ? <p className="text-sm text-destructive">{issue}</p> : null}
    </div>
  );
}
